import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, TextField, ThemeProvider, CssBaseline, IconButton, InputAdornment, List, ListItem, ListItemIcon, ListItemText, CircularProgress } from '@mui/material';
import { Visibility, VisibilityOff, CheckCircleOutline, RadioButtonUnchecked } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { theme } from '../theme/theme';
import { registerUser, RegisterInterface } from '../services/auth_request'; // Importar registerUser y RegisterInterface

const validationSchema = yup.object({
  first_name: yup
    .string()
    .required('El nombre es requerido'),
  last_name: yup
    .string()
    .required('El apellido es requerido'),
  email: yup
    .string()
    .email('Ingresa un email válido')
    .required('El email es requerido'),
  password: yup
    .string()
    .min(8, 'La contraseña debe tener al menos 8 caracteres')
    .matches(/[A-Z]/, 'Debe contener al menos una letra mayúscula')
    .matches(/[0-9]/, 'Debe contener al menos un número')
    .matches(/[^a-zA-Z0-9]/, 'Debe contener al menos un carácter especial')
    .required('La contraseña es requerida'),
  password2: yup
    .string()
    .oneOf([yup.ref('password')], 'Las contraseñas deben coincidir')
    .required('Confirma tu contraseña'),
});

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [loading, setLoading] = useState(false);

  const [hasMinLength, setHasMinLength] = useState(false);
  const [hasUpperCase, setHasUpperCase] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [hasSpecialChar, setHasSpecialChar] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowPassword2 = () => setShowPassword2((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const formik = useFormik({
    initialValues: {
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      password2: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      const username = `${values.first_name.toLowerCase()}.${values.last_name.toLowerCase()}`; // Generar username
      const registerData: RegisterInterface = {
        pre_reg_first_name: values.first_name,
        pre_reg_last_name: values.last_name,
        pre_reg_username: username, // Usar el username generado
        pre_reg_email: values.email,
        pre_reg_password: values.password,
      };

      try {
        const status = await registerUser(registerData);
        if (status === 200) {

          navigate('/verify-email', { state: { email: values.email } });
        } else {
          alert(`Error en el registro. Código: ${status}`);
        }
      } catch (error) {
        alert('Ocurrió un error al intentar registrarse.');
        console.error('Error de registro:', error);
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    const password = formik.values.password;
    setHasMinLength(password.length >= 8);
    setHasUpperCase(/[A-Z]/.test(password));
    setHasNumber(/[0-9]/.test(password));
    setHasSpecialChar(/[^a-zA-Z0-9]/.test(password));
  }, [formik.values.password]);

  const passwordCriteria = [
    { label: 'Al menos 8 caracteres', met: hasMinLength },
    { label: 'Al menos una letra mayúscula', met: hasUpperCase },
    { label: 'Al menos un número', met: hasNumber },
    { label: 'Al menos un carácter especial', met: hasSpecialChar },
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: theme.palette.text.primary }}>
          Registrarse
        </Typography>
        <Typography variant="body1" mb={3} sx={{ color: theme.palette.text.secondary }}>
          Crea una cuenta para acceder a todas las funciones.
        </Typography>
        <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
          <form onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              id="first_name"
              name="first_name"
              label="Nombre"
              value={formik.values.first_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.first_name && Boolean(formik.errors.first_name)}
              helperText={formik.touched.first_name && formik.errors.first_name}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              id="last_name"
              name="last_name"
              label="Apellido"
              value={formik.values.last_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.last_name && Boolean(formik.errors.last_name)}
              helperText={formik.touched.last_name && formik.errors.last_name}
              sx={{ mb: 2 }}
            />
            {/* Eliminado el TextField de username */}
            <TextField
              fullWidth
              id="email"
              name="email"
              label="Email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              id="password"
              name="password"
              label="Contraseña"
              type={showPassword ? 'text' : 'password'}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            <List sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.paper', ml: 0, mr: 'auto', mb: 2 }}>
              {passwordCriteria.map((criterion, index) => (
                <ListItem key={index} disablePadding>
                  <ListItemIcon>
                    {criterion.met ? <CheckCircleOutline color="success" /> : <RadioButtonUnchecked color="error" />}
                  </ListItemIcon>
                  <ListItemText primary={criterion.label} sx={{ textAlign: 'left' }} />
                </ListItem>
              ))}
            </List>
            <TextField
              fullWidth
              id="password2"
              name="password2"
              label="Confirmar Contraseña"
              type={showPassword2 ? 'text' : 'password'}
              value={formik.values.password2}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password2 && Boolean(formik.errors.password2)}
              helperText={formik.touched.password2 && formik.errors.password2}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password2 visibility"
                      onClick={handleClickShowPassword2}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword2 ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />
            <Button color="primary" variant="contained" fullWidth type="submit" disabled={loading}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Registrarse'}
            </Button>
          </form>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Register;


